import React, {createContext, useContext, useState, useEffect} from 'react';
import {updateMetaTags} from '../utils/seoUtils';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// 動態導入語言文件
const loadLanguageFile = async (lang) => {
  try {
    const translations = await import(`../locales/${lang}.json`);
    return translations.default;
  } catch (error) {
    console.error(`Failed to load language file for ${lang}:`, error);
    const fallback = await import('../locales/zh.json');
    return fallback.default;
  }
};

export const LanguageProvider = ({children}) => {
  const [currentLang, setCurrentLang] = useState('zh');
  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // 初始化語言
  useEffect(() => {
    const initLanguage = async () => {
      // 1. 檢測當前 URL 路徑
      const pathname = window.location.pathname;
      let langToLoad = 'zh';
      const supportedLangs = ['zh', 'en', 'ja', 'es'];

      // 🔥 檢測語言目錄 (例如 /en/, /ja/, /es/)
      const match = pathname.match(/^\/([a-z]{2})\/?/);
      if (match && supportedLangs.includes(match[1])) {
        langToLoad = match[1];
      } else {
        // 檢查 URL 參數
        const urlParams = new URLSearchParams(window.location.search);
        const urlLang = urlParams.get('lang');
        const savedLang = localStorage.getItem('cm-language');
        const browserLang = navigator.language.split('-')[0];

        if (urlLang && supportedLangs.includes(urlLang)) {
          langToLoad = urlLang;
        } else if (savedLang && supportedLangs.includes(savedLang)) {
          langToLoad = savedLang;
        } else if (supportedLangs.includes(browserLang)) {
          langToLoad = browserLang;
        }
      }

      setCurrentLang(langToLoad);
      const langData = await loadLanguageFile(langToLoad);
      setTranslations(langData);
      setIsLoading(false);

      // 更新 Meta 標籤
      updateMetaTags(langToLoad);
    };

    initLanguage();
  }, []);

  // 🔥 修改語言切換邏輯 - 導航到乾淨的 URL
  const changeLanguage = (lang) => {
    console.log('🔄 Changing language to:', lang);
    
    // 保存語言偏好
    localStorage.setItem('cm-language', lang);

    // 🔥 關鍵修改:使用 window.location.replace 強制導航
    const currentHash = window.location.hash || '';
    
    if (lang === 'zh') {
      // 中文使用根路徑
      const newUrl = '/' + currentHash;
      console.log('➡️ Replacing location to:', newUrl);
      window.location.replace(newUrl);
    } else {
      // 其他語言導航到語言目錄
      const newUrl = `/${lang}/${currentHash}`;
      console.log('➡️ Replacing location to:', newUrl);
      window.location.replace(newUrl);
    }
  };

  const t = (key) => {
    return translations[key] || key;
  };

  const value = {
    currentLang,
    changeLanguage,
    t,
    isLoading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};